import * as React from "react";
import { Theme, useTheme } from "@mui/material/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import FormHelperText from "@mui/material/FormHelperText";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface PropType {
  inputLabel?: string;
  validateStatus?: "" | "success" | "warning" | "error" | "validating";
  help?: string;
  placeHolderLabel?: string;
  value: string | string[]; // This could accept both single and multiple values
  type?: "string" | "number";
  style?: CSSProperties;
  disabled?: boolean;
  tooltipText?: string;
  placement?: "top" | "left" | "right" | "bottom";
  setParentState: (value: string | string[]) => void; // This could set both single and multiple values
  multiple?: boolean; // Added flag for multiple selection
  options: Array<{ id: string; name: string }>;
  error?: boolean | undefined;
  labelStyle?: CSSProperties;
}

function getStyles(
  name: string,
  selectedValues: readonly string[],
  theme: Theme,
) {
  return {
    fontWeight: selectedValues.includes(name)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const SelectList: React.FC<PropType> = ({
  inputLabel = "",
  placeHolderLabel = "",
  value,
  setParentState,
  style = {},
  disabled = false,
  tooltipText = "",
  labelStyle = {},
  help = "",
  multiple = false, // Default is single selection
  options = [],
  error = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<typeof value>) => {
    const {
      target: { value },
    } = event;
    if (multiple) {
      setParentState(typeof value === "string" ? value.split(",") : value);
    } else {
      setParentState(value as string);
    }
  };

  return (
    <Tooltip
      title={tooltipText || ""}
      placement={tooltipText ? "top" : undefined}
    >
      <div style={style}>
        {inputLabel && (
          <Typography
            className="custom-label"
            variant="body1"
            style={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "16px",
              color: "white",
              ...labelStyle,
            }}
          >
            {inputLabel}
          </Typography>
        )}

        <FormControl
          error={error}
          sx={{ width: "100%", mt: 2 }}
          disabled={disabled}
        >
          <Select
            multiple={multiple}
            displayEmpty
            value={value}
            onChange={handleChange}
            input={<OutlinedInput />}
            MenuProps={MenuProps}
            inputProps={{
              "aria-label": placeHolderLabel || "Select input",
            }}
            sx={{
              "& .MuiInputBase-input": {
                height: 7,
                paddingLeft: 1.5,
              },
              width: "100%",
              background: "white",
              height: "40px",

              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
          >
            {options.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                style={getStyles(
                  String(option.name),
                  typeof value === "string" ? [value] : value,
                  theme,
                )}
              >
                {t(option.name)}
              </MenuItem>
            ))}
          </Select>
          {error && help && <FormHelperText>{t(help)}</FormHelperText>}
        </FormControl>
      </div>
    </Tooltip>
  );
};

export default React.memo(SelectList);
